/**
 * The plan for ONE replay run — pure, zero I/O.
 *
 * Two independent jobs share this file because they are two halves of the
 * same question ("what would this run actually execute"):
 *
 *   - {@link readPreflight} reads `pipelineExecutor.validatePipeline`'s
 *     verdict and REFUSES a run that would silently execute on a substituted
 *     model. `runPipeline` itself never refuses — `resolveModelForFormat`
 *     swaps a model with no build for the node's format for that format's
 *     default, in silence, with an info log
 *     (`stateless-step.ts:96-107` is the whole reason `runStatelessStep`
 *     exists). For live traffic that is the right call — a degraded detector
 *     beats no detector. For a REPLAY it is the worst possible outcome: two
 *     runs that differ only in one threshold would show a delta actually
 *     produced by two different encoders, and nothing in the result would
 *     say so.
 *
 *   - {@link buildReplayPlan} turns the camera's RESOLVED step tree plus an
 *     explicit operator overlay into the tree `runPipeline` executes. Without
 *     overlay it reproduces the resolved tree UNCHANGED — a replay's baseline
 *     run must be what the camera actually does, or the comparison measures
 *     the plan's own normalization instead of the operator's question.
 *
 * `package-detection`'s activation rule (`applyPackageZoneActivation`,
 * `packages/addon-pipeline-orchestrator/src/pipeline-resolver.ts:62`) is
 * REPLICATED here, not imported — addons do not import each other, and this
 * plan lives in `addon-benchmark`. The two implementations are pinned by
 * near-identical tests on purpose: if production's rule ever changes, this
 * file's test drifts from `package-zone-activation.spec.ts`, which is
 * exactly the signal that the replication needs updating.
 */
import type { CameraPipelineConfig, PipelineStepInputOutput } from '@camstack/types'

// ── Task 0.2 — preflight ────────────────────────────────────────────────

/** The subset of `PipelineValidationResult` this module reads. Named
 *  locally (rather than importing the cap's zod-inferred type) so this pure
 *  module has no dependency on the capability layer at all — any shape with
 *  these fields works, including a hand-built test fixture. */
export interface ReplayPreflightInput {
  readonly ok: boolean
  readonly issues: readonly {
    readonly addonId: string
    readonly kind: string
    readonly detail: string
  }[]
  readonly substitutions: readonly {
    readonly addonId: string
    readonly chosen: string
    readonly running: string
    readonly format: string
  }[]
  readonly format: string
}

export type ReplayPreflightVerdict =
  | { readonly kind: 'runnable'; readonly format: string }
  | {
      readonly kind: 'refused'
      readonly reason: 'model-substituted' | 'step-unrunnable'
      readonly detail: string
    }

/**
 * Read a preflight verdict. A hard issue (`ok: false`) always wins over a
 * substitution: if the step cannot run at all, reporting "model substituted"
 * would send the operator to tune a model that was never going to load.
 */
export function readPreflight(result: ReplayPreflightInput): ReplayPreflightVerdict {
  if (!result.ok) {
    const detail = result.issues.map((i) => `${i.addonId}: ${i.detail}`).join('; ')
    return { kind: 'refused', reason: 'step-unrunnable', detail }
  }
  if (result.substitutions.length > 0) {
    const detail = result.substitutions
      .map((s) => `${s.addonId}: chose \`${s.chosen}\`, running \`${s.running}\` (${s.format})`)
      .join('; ')
    return { kind: 'refused', reason: 'model-substituted', detail }
  }
  return { kind: 'runnable', format: result.format }
}

// ── Task B1 — the run plan ──────────────────────────────────────────────

/** `package-detection`'s root step id — replicated from
 *  `PACKAGE_DETECTION_STEP_ID` in `pipeline-resolver.ts` (addons don't
 *  import each other). */
const PACKAGE_DETECTION_STEP_ID = 'package-detection'

/**
 * Explicit operator overlay on top of the camera's resolved tree. Every
 * field absent ⇒ the baseline run (the tree as production would execute
 * it) — see {@link buildReplayPlan}.
 */
export interface ReplayOverlay {
  /**
   * Run `package-detection` this pass, or keep it out of the tree.
   *
   * **A replay cannot express a package RECTANGLE.** This used to be
   * `packageZone: {x,y,w,h}`, described as "the ephemeral zone the operator
   * draws on a replay's first frame". Nothing drew it and nothing read it:
   * only `packageZone !== undefined` was ever consulted, the rectangle never
   * reached `plan.steps`, and there was nowhere for it to go — a
   * `crop-zone` root takes its crop from the DEVICE's stored zones and
   * `package`-stage zone rules, read off the device-state mirror at execution
   * time (`crop-zone.ts:resolvePackageCropBbox`), not from step settings. An
   * operator "drawing a zone" therefore flipped a boolean and changed no
   * geometry. Injecting an ephemeral zone into the executor's device-state
   * read is a feature, not a field; so this says exactly what it does.
   *
   * `true` ensures the step is present and enabled (cropping to whatever
   * package zones the camera actually has — none means full-frame inference,
   * as in production); `false` strips it; ABSENT leaves the resolved tree
   * alone, which is what "absent ⇒ the baseline run" above promises and what
   * the rectangle's presence-test broke: an overlay that only moved an
   * `object-detection` slider silently deleted `package-detection` from a
   * camera that runs it in production, so the "baseline" a delta was measured
   * against was not the baseline.
   */
  readonly packageDetection?: boolean
  /** Per-step settings patch, keyed by `addonId`. Merged onto the step's
   *  existing `settings`, never replacing the whole object — an operator
   *  moving one slider must not blank out every other one the resolved tree
   *  already carried. */
  readonly stepSettings?: Readonly<Record<string, Readonly<Record<string, unknown>>>>
  /** Explicit model override, keyed by `addonId`. */
  readonly stepModels?: Readonly<Record<string, string>>
}

export interface ReplayPlan {
  readonly steps: readonly PipelineStepInputOutput[]
  /** Overlay entries that named a step the tree does not currently run
   *  (most commonly: a `stepSettings` patch for `package-detection` on a
   *  camera that does not run it, with no `packageDetection: true` to turn it
   *  on). Reported rather than silently dropped — an operator who moved a
   *  slider that never took effect needs to be told, not left to wonder why
   *  the delta shows nothing. */
  readonly ignoredOverlays: readonly string[]
}

/** Replicates `applyPackageZoneActivation` — see the file header. */
function applyPackageZoneOverlay(
  steps: readonly PipelineStepInputOutput[],
  overlay: ReplayOverlay,
): readonly PipelineStepInputOutput[] {
  // Absent is the baseline: the resolved tree already answers this question
  // for the camera, and an overlay that says nothing about packages must not
  // overrule it.
  if (overlay.packageDetection === undefined) return steps
  const existing = steps.find((s) => s.addonId === PACKAGE_DETECTION_STEP_ID)
  if (!overlay.packageDetection) {
    if (!existing) return steps
    return steps.filter((s) => s.addonId !== PACKAGE_DETECTION_STEP_ID)
  }
  if (existing) {
    if (existing.enabled === true) return steps
    return steps.map((s) => (s.addonId === PACKAGE_DETECTION_STEP_ID ? { ...s, enabled: true } : s))
  }
  const node: PipelineStepInputOutput = {
    addonId: PACKAGE_DETECTION_STEP_ID,
    enabled: true,
    children: [],
  }
  return [...steps, node]
}

/** Apply `stepSettings` / `stepModels` onto steps ALREADY present in the
 *  tree; a step named in the overlay that the tree does not run is reported
 *  in `ignoredOverlays` rather than silently dropped. */
function applySettingsAndModels(
  steps: readonly PipelineStepInputOutput[],
  overlay: ReplayOverlay,
  ignored: Set<string>,
): readonly PipelineStepInputOutput[] {
  const settingsKeys = new Set(Object.keys(overlay.stepSettings ?? {}))
  const modelKeys = new Set(Object.keys(overlay.stepModels ?? {}))
  const presentIds = new Set(steps.map((s) => s.addonId))

  for (const id of settingsKeys) if (!presentIds.has(id)) ignored.add(id)
  for (const id of modelKeys) if (!presentIds.has(id)) ignored.add(id)

  return steps.map((s) => {
    const settingsPatch = overlay.stepSettings?.[s.addonId]
    const modelOverride = overlay.stepModels?.[s.addonId]
    if (settingsPatch === undefined && modelOverride === undefined) return s
    return {
      ...s,
      ...(modelOverride !== undefined ? { modelId: modelOverride } : {}),
      ...(settingsPatch !== undefined ? { settings: { ...s.settings, ...settingsPatch } } : {}),
    }
  })
}

/**
 * Build the tree `runPipeline` executes for one replay run, from the
 * camera's RESOLVED step tree (`pipelineOrchestrator.resolvePipeline` →
 * `resolvedStepsToRunSteps`) plus an explicit overlay.
 */
export function buildReplayPlan(base: CameraPipelineConfig, overlay: ReplayOverlay): ReplayPlan {
  const ignored = new Set<string>()
  const withPackageZone = applyPackageZoneOverlay(base.steps, overlay)
  // A settings/model patch aimed at a step this pass will not run is reported
  // by `applySettingsAndModels` from the step's ABSENCE, `package-detection`
  // included. It used to be special-cased here off "no package zone drawn",
  // which now reports the opposite of the truth: a camera that runs
  // `package-detection` in production runs it in an overlay-free replay too,
  // and a patch for it applies.
  const steps = applySettingsAndModels(withPackageZone, overlay, ignored)
  return { steps, ignoredOverlays: [...ignored] }
}
